import { useState, useEffect } from 'react';
import { Plus, Clock, Filter, FileSpreadsheet, Calendar, User, Briefcase, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { api } from '../../core/apiClient';
import { formatCurrency, formatDate } from '../../lib/utils';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Employee {
  id: string;
  code: string;
  firstName: string;
  lastName: string;
  hourlyRate?: number;
}

interface WorkLog {
  id: string;
  employeeId: string;
  employee?: Employee;
  workOrderId?: string;
  projectId?: string;
  workDate: string;
  hours: number;
  taskType: string;
  description?: string;
  hourlyCost?: number;
  totalCost?: number;
}

interface WorkOrder {
  id: string;
  orderNumber: string;
  description?: string;
  projectId?: string;
}

interface Project {
  id: string;
  projectNumber: string;
  name: string;
}

// Valors alineats amb l'enum real de la columna `task_type` a la BD
// (DESCRIBE work_logs, 2026-09-11) — abans hi havia un enum en català que
// no coincidia amb cap valor real i feia fallar qualsevol alta.
const TASK_TYPES = [
  { value: 'cutting', label: 'Tall' },
  { value: 'welding', label: 'Soldadura' },
  { value: 'assembly', label: 'Muntatge' },
  { value: 'painting', label: 'Pintura' },
  { value: 'installation', label: 'Instal·lació' },
  { value: 'design', label: 'Disseny' },
  { value: 'planning', label: 'Planificació' },
  { value: 'supervision', label: 'Supervisió' },
  { value: 'transport', label: 'Transport' },
  { value: 'maintenance', label: 'Manteniment' },
  { value: 'admin', label: 'Administració' },
  { value: 'meeting', label: 'Reunió' },
  { value: 'other', label: 'Altres' },
];

export default function WorkLogs() {
  const navigate = useNavigate();
  const [workLogs, setWorkLogs] = useState<WorkLog[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState<WorkLog | null>(null);
  const [summary, setSummary] = useState<{
    totalHours: number;
    totalCost: number;
    byTaskType: Record<string, { hours: number; cost: number }>;
  }>({ totalHours: 0, totalCost: 0, byTaskType: {} });

  // Filtres
  const [filters, setFilters] = useState({
    employeeId: '',
    projectId: '',
    workOrderId: '',
    startDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    taskType: '',
  });

  // Form
  const [formData, setFormData] = useState({
    employeeId: '',
    workOrderId: '',
    projectId: '',
    workDate: new Date().toISOString().split('T')[0],
    hours: '',
    taskType: 'other',
    description: '',
    hourlyRate: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadWorkLogs();
  }, [filters]);

  const loadData = async () => {
    try {
      const [empRes, woRes, projRes] = await Promise.all([
        api.get<{ data: Employee[] }>('/employees'),
        api.get<{ data: WorkOrder[] }>('/work-orders'),
        api.get<{ data: Project[] }>('/taller/projects'),
      ]);
      setEmployees((empRes.data as any)?.data || []);
      setWorkOrders((woRes.data as any)?.data || []);
      setProjects((projRes.data as any)?.data || []);
    } catch (error) {
      console.error('[v0] Error loading data:', error);
    }
  };

  const loadWorkLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.employeeId) params.append('employeeId', filters.employeeId);
      if (filters.projectId) params.append('projectId', filters.projectId);
      if (filters.workOrderId) params.append('workOrderId', filters.workOrderId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.taskType) params.append('taskType', filters.taskType);

      const response = await api.get<any>(`/work-logs?${params.toString()}`);
      setWorkLogs(response.data?.data || []);
      setSummary(response.data?.summary || { totalHours: 0, totalCost: 0, byTaskType: {} });
    } catch (error) {
      console.error('[v0] Error loading work logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        employeeId: formData.employeeId,
        workOrderId: formData.workOrderId || undefined,
        projectId: formData.projectId || undefined,
        workDate: formData.workDate,
        hours: parseFloat(formData.hours),
        taskType: formData.taskType,
        description: formData.description || undefined,
        hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : undefined,
      };

      if (editingLog) {
        await api.put(`/work-logs/${editingLog.id}`, payload);
      } else {
        await api.post('/work-logs', payload);
      }

      setShowModal(false);
      setEditingLog(null);
      resetForm();
      loadWorkLogs();
    } catch (error) {
      console.error('[v0] Error saving work log:', error);
    }
  };

  const handleEdit = (log: WorkLog) => {
    setEditingLog(log);
    setFormData({
      employeeId: log.employeeId,
      workOrderId: log.workOrderId || '',
      projectId: log.projectId || '',
      workDate: log.workDate,
      hours: String(log.hours),
      taskType: log.taskType,
      description: log.description || '',
      hourlyRate: log.hourlyCost ? String(log.hourlyCost) : '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Segur que vols eliminar aquest registre?')) return;
    try {
      await api.delete(`/work-logs/${id}`);
      loadWorkLogs();
    } catch (error) {
      console.error('[v0] Error deleting work log:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      workOrderId: '',
      projectId: '',
      workDate: new Date().toISOString().split('T')[0],
      hours: '',
      taskType: 'other',
      description: '',
      hourlyRate: '',
    });
  };

  const handleEmployeeChange = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    setFormData(prev => ({
      ...prev,
      employeeId,
      hourlyRate: employee?.hourlyRate ? String(employee.hourlyRate) : prev.hourlyRate,
    }));
  };

  const handleWorkOrderChange = (workOrderId: string) => {
    const wo = workOrders.find(w => w.id === workOrderId);
    setFormData(prev => ({
      ...prev,
      workOrderId,
      // L'OT ja porta el seu propi projecte — s'autoemplena per mantenir
      // coherent la parella projecte/OT del registre.
      projectId: wo?.projectId || prev.projectId,
    }));
  };

  const getTaskTypeLabel = (type: string) => {
    return TASK_TYPES.find(t => t.value === type)?.label || type;
  };

  const getTaskTypeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      cutting: 'bg-red-100 text-red-800',
      welding: 'bg-orange-100 text-orange-800',
      assembly: 'bg-blue-100 text-blue-800',
      painting: 'bg-purple-100 text-purple-800',
      installation: 'bg-indigo-100 text-indigo-800',
      design: 'bg-cyan-100 text-cyan-800',
      planning: 'bg-teal-100 text-teal-800',
      supervision: 'bg-fuchsia-100 text-fuchsia-800',
      transport: 'bg-lime-100 text-lime-800',
      maintenance: 'bg-amber-100 text-amber-800',
      admin: 'bg-sky-100 text-sky-800',
      meeting: 'bg-pink-100 text-pink-800',
      other: 'bg-slate-100 text-slate-800',
    };
    return colors[type] || colors.other;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/rrhh')}
            title="Tornar enrere"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Imputació d'Hores</h1>
            <p className="text-slate-500">Registra hores treballades per projecte i ordre de treball</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setEditingLog(null); setShowModal(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Registrar Hores
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Hores</p>
                <p className="text-2xl font-bold">{summary.totalHours.toFixed(1)}h</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Cost Total</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.totalCost)}</p>
              </div>
              <FileSpreadsheet className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Cost Mig/Hora</p>
                <p className="text-2xl font-bold">
                  {summary.totalHours > 0 
                    ? formatCurrency(summary.totalCost / summary.totalHours)
                    : '0,00 €'
                  }
                </p>
              </div>
              <Briefcase className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Registres</p>
                <p className="text-2xl font-bold">{workLogs.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="rounded-xl">
        <CardHeader className="pb-3 border-b">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-3">
            <div>
              <Label>Data Inici</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Data Fi</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Empleat</Label>
              <ExcelFilterDropdown
                label="Empleat"
                options={employees.map(emp => ({ value: emp.id, label: `${emp.firstName} ${emp.lastName}` }))}
                selected={filters.employeeId ? filters.employeeId.split(',') : []}
                onApply={(values) => setFilters(prev => ({ ...prev, employeeId: values.join(',') }))}
                className="w-full"
              />
            </div>
            <div>
              <Label>Projecte</Label>
              <ExcelFilterDropdown
                label="Projecte"
                options={projects.map(proj => ({ value: proj.id, label: `${proj.projectNumber} - ${proj.name}` }))}
                selected={filters.projectId ? filters.projectId.split(',') : []}
                onApply={(values) => setFilters(prev => ({ ...prev, projectId: values.join(',') }))}
                className="w-full"
              />
            </div>
            <div>
              <Label>Tipus Tasca</Label>
              <ExcelFilterDropdown
                label="Tipus"
                options={TASK_TYPES.map(type => ({ value: type.value, label: type.label }))}
                selected={filters.taskType ? filters.taskType.split(',') : []}
                onApply={(values) => setFilters(prev => ({ ...prev, taskType: values.join(',') }))}
                className="w-full"
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => setFilters({
                employeeId: '',
                projectId: '',
                workOrderId: '',
                startDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                taskType: '',
              })}>
                Netejar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-xl">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Empleat</TableHead>
                <TableHead>Projecte / OT</TableHead>
                <TableHead>Tasca</TableHead>
                <TableHead className="text-right">Hores</TableHead>
                <TableHead className="text-right">Tarifa</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Accions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Carregant...
                  </TableCell>
                </TableRow>
              ) : workLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No hi ha registres d'hores per als filtres seleccionats
                  </TableCell>
                </TableRow>
              ) : (
                workLogs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell>{formatDate(log.workDate)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {log.employee?.firstName} {log.employee?.lastName}
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.projectId && (
                        <span className="text-sm">
                          {projects.find(p => p.id === log.projectId)?.projectNumber || '-'}
                        </span>
                      )}
                      {log.workOrderId && (
                        <span className="text-xs text-slate-500 block">
                          OT: {workOrders.find(w => w.id === log.workOrderId)?.orderNumber || '-'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getTaskTypeBadgeColor(log.taskType)}>
                        {getTaskTypeLabel(log.taskType)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {Number(log.hours).toFixed(1)}h
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(Number(log.hourlyCost || 0))}/h
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(Number(log.totalCost || 0))}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(log)}>
                          Editar
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(log.id)}>
                          Eliminar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal alta/edició */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLog ? 'Editar registre' : 'Registrar hores'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Empleat *</Label>
                <select
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  value={formData.employeeId}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                >
                  <option value="">Selecciona...</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Data *</Label>
                <Input
                  type="date"
                  required
                  value={formData.workDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, workDate: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ordre de Treball</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  value={formData.workOrderId}
                  onChange={(e) => handleWorkOrderChange(e.target.value)}
                >
                  <option value="">Sense OT</option>
                  {workOrders.map(wo => (
                    <option key={wo.id} value={wo.id}>{wo.orderNumber} — {wo.description || ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Projecte</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  value={formData.projectId}
                  onChange={(e) => setFormData(prev => ({ ...prev, projectId: e.target.value }))}
                >
                  <option value="">Sense projecte</option>
                  {projects.map(proj => (
                    <option key={proj.id} value={proj.id}>{proj.projectNumber} - {proj.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Hores *</Label>
                <Input
                  type="number"
                  step="0.25"
                  min="0.25"
                  max="24"
                  required
                  value={formData.hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                  placeholder="0,00"
                />
              </div>
              <div>
                <Label>Tipus de tasca</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                  value={formData.taskType}
                  onChange={(e) => setFormData(prev => ({ ...prev, taskType: e.target.value }))}
                >
                  {TASK_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Tarifa (€/h)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData(prev => ({ ...prev, hourlyRate: e.target.value }))}
                  placeholder="Cost de l'empleat"
                />
              </div>
            </div>

            <div>
              <Label>Descripció</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripció del treball realitzat..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingLog(null); resetForm(); }}>
                Cancel·lar
              </Button>
              <Button type="submit">
                {editingLog ? 'Desar canvis' : 'Registrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
